import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { countBlanks, isAllBlanksFilled } from '../../../utils/quiz-parser'
import type { FillBlankQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

export interface UseFillBlankQuizOptions {
  quizId: string | number
  quiz: FillBlankQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

function parseAnswerKey(key: string): string[] {
  try {
    const parsed = JSON.parse(key)
    return Array.isArray(parsed) ? parsed.map((value) => String(value)) : []
  } catch {
    return []
  }
}

export function useFillBlankQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseFillBlankQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const blankCount = useMemo(() => countBlanks(quiz.problem), [quiz.problem])
  const isSolved = solveResult !== null && solveResult !== undefined
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers
  const hasInitialAnswers = initialAnswersProp !== undefined
  const initialAnswersKey = JSON.stringify(initialAnswersProp ?? [])
  const correctAnswersKey = JSON.stringify(solveResult?.correctAnswers ?? [])

  const computedInitialAnswers = useMemo(() => {
    const initialAnswers = parseAnswerKey(initialAnswersKey)
    if (hasInitialAnswers) {
      return new Array(blankCount).fill('').map((_, index) => initialAnswers[index] ?? '')
    }
    const correctAnswers = parseAnswerKey(correctAnswersKey)
    if (isViewSolved && correctAnswers.length > 0) {
      return correctAnswers
    }
    return new Array(blankCount).fill('')
  }, [blankCount, correctAnswersKey, hasInitialAnswers, initialAnswersKey, isViewSolved])

  const [answers, setAnswers] = useState<string[]>(computedInitialAnswers)
  const onAnswerChangeRef = useRef(onAnswerChange)
  const canSubmit = isAllBlanksFilled(answers)

  useEffect(() => {
    onAnswerChangeRef.current = onAnswerChange
  }, [onAnswerChange])

  useEffect(() => {
    onAnswerChangeRef.current?.(answers)
  }, [answers])

  useEffect(() => {
    setAnswers(prev => {
      if (
        prev.length === computedInitialAnswers.length &&
        prev.every((value, index) => value === computedInitialAnswers[index])
      ) {
        return prev
      }
      return computedInitialAnswers
    })
  }, [computedInitialAnswers])

  const blankKeys = useMemo<Map<number, string>>(() => {
    const keysMap = new Map<number, string>()
    const answerReg = /\{\{([0-9]+)?}}/gim
    let match
    let blankIndex = 0
    while ((match = answerReg.exec(quiz.problem)) !== null) {
      const key = match[1] || ''
      keysMap.set(blankIndex, key)
      blankIndex++
    }
    return keysMap
  }, [quiz.problem])

  const handleBlankClick = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setCurrentIndex(index)
  }, [isSolved, isViewSolved])

  const handleInputChange = useCallback((index: number, value: string, key?: string) => {
    if (isSolved || isViewSolved) return
    setAnswers(prev => {
      const newAnswers = [...prev]
      newAnswers[index] = value
      if (key && key !== '') {
        blankKeys.forEach((blankKey, blankIndex) => {
          if (blankKey === key && blankIndex !== index) {
            newAnswers[blankIndex] = value
          }
        })
      }
      return newAnswers
    })
  }, [isSolved, isViewSolved, blankKeys])

  const handleKeypadInput = useCallback((value: string) => {
    if (isSolved || isViewSolved) return
    const currentKey = blankKeys.get(currentIndex) || ''
    setAnswers(prev => {
      const newAnswers = [...prev]
      newAnswers[currentIndex] = value
      if (currentKey && currentKey !== '') {
        blankKeys.forEach((blankKey, blankIndex) => {
          if (blankKey === currentKey && blankIndex !== currentIndex) {
            newAnswers[blankIndex] = value
          }
        })
      }
      return newAnswers
    })
    if (currentIndex < blankCount - 1) {
      setCurrentIndex(prev => prev + 1)
    }
  }, [currentIndex, blankCount, isSolved, isViewSolved, blankKeys])

  const handleKeypadClear = useCallback(() => {
    if (isSolved || isViewSolved) return
    const currentKey = blankKeys.get(currentIndex) || ''
    setAnswers(prev => {
      const newAnswers = [...prev]
      newAnswers[currentIndex] = ''
      if (currentKey && currentKey !== '') {
        blankKeys.forEach((blankKey, blankIndex) => {
          if (blankKey === currentKey && blankIndex !== currentIndex) {
            newAnswers[blankIndex] = ''
          }
        })
      }
      return newAnswers
    })
  }, [currentIndex, isSolved, isViewSolved, blankKeys])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, answers, quiz.vars, elapsedTime)
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime])

  return {
    answers,
    currentIndex,
    blankCount,
    blankKeys,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleBlankClick,
    handleInputChange,
    handleKeypadInput,
    handleKeypadClear,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
